<script setup>
/**
 * Jornada de trabalho do agente (Portela, 19/10/2026) — fase 3 do
 * ESTUDO-relatorio-por-agente.md.
 *
 * O Chatwoot só tem horário de funcionamento da CAIXA DE ENTRADA. Aqui se
 * cadastra o do AGENTE — "segunda a sexta das 8 às 18, sábado até meio-dia" —
 * que é o que dá escala ao tempo online: "7h30 de 9h" em vez de "7h30" solto.
 *
 * Dois modos:
 *  - EDIÇÃO/RELATÓRIO (`userId` presente): busca a jornada gravada, semana
 *    FECHADA como baseline p/ quem não tem, e salva sozinho pelo botão próprio.
 *    Fica recolhido por padrão — é consulta de vez em quando, e aberto empurraria
 *    o extrato (que é o que se vem ver) para baixo da dobra.
 *  - CRIAÇÃO (`userId` ausente, Portela 2026-10-21): o agente ainda não existe,
 *    então não há o que buscar. Começa do PADRÃO comercial brasileiro e NÃO tem
 *    botão próprio — quem grava é o submit do modal de criar agente, que chama
 *    `salvarPara(novoId)` já com o id recém-criado. Abre expandido p/ o padrão
 *    aparecer. O padrão vale SÓ aqui, onde de fato será salvo (na edição,
 *    pré-preencher mostraria horário sem nada gravado — tela que mente).
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AgentScheduleAPI from 'dashboard/api/agentSchedule';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  // Opcional: na criação o agente ainda não existe, logo não há id.
  userId: { type: [Number, String], default: null },
  // Botão próprio "Salvar jornada". No relatório o componente vive sozinho e
  // precisa dele; no modal de editar agente (`false`) quem grava é o submit do
  // modal, pra não haver DOIS botões de salvar na mesma tela.
  mostrarBotao: { type: Boolean, default: true },
});

const { t } = useI18n();

const DIAS = [1, 2, 3, 4, 5, 6, 0]; // segunda primeiro: é como se lê uma escala

// Opções do seletor de hora: de 30 em 30 min, 00:00 → 23:30.
const pad = n => String(n).padStart(2, '0');
const hhmm = (h, m) => `${pad(h)}:${pad(m)}`;
const BASE_HORAS = Array.from({ length: 48 }, (_, i) => hhmm(Math.floor(i / 2), (i % 2) * 30));
// Preserva valor salvo fora do passo de 30 min (ex.: 08:15 que o campo numérico
// antigo permitia): injeta como opção p/ aparecer selecionado e sobreviver ao
// salvamento — nunca cair no vizinho mais próximo em silêncio.
const opcoesHora = (h, m) => {
  const v = hhmm(h, m);
  return BASE_HORAS.includes(v) ? BASE_HORAS : [...BASE_HORAS, v].sort();
};

// Sem id => criação. É o único sinal de que precisamos: edição e relatório
// sempre entram com um id concreto (v-if garante na tela do pai).
const modoCriacao = computed(() => !props.userId);

const aberto = ref(modoCriacao.value); // criação abre expandida; o resto, recolhido
const salvando = ref(false);
const semana = ref([]);
const fuso = ref(''); // vem do servidor; vazio => linha de fuso não aparece

// Trava anti-apagão (ver precisaSalvar): só grava quando há motivo.
const carregouOk = ref(false); // GET da jornada teve sucesso
const sujo = ref(false); // o usuário mexeu em algo depois de carregar

const diaVazio = dayOfWeek => ({
  day_of_week: dayOfWeek,
  open_hour: 8,
  open_minutes: 0,
  close_hour: 18,
  close_minutes: 0,
  closed_all_day: true,
});

// Padrão comercial brasileiro (decidido com o dono): seg–sex 08:00–18:00,
// sábado 08:00–12:00, domingo folga. Assim nenhum agente nasce sem jornada.
const diaPadrao = dayOfWeek => {
  if (dayOfWeek >= 1 && dayOfWeek <= 5) {
    return { day_of_week: dayOfWeek, open_hour: 8, open_minutes: 0, close_hour: 18, close_minutes: 0, closed_all_day: false };
  }
  if (dayOfWeek === 6) {
    return { day_of_week: 6, open_hour: 8, open_minutes: 0, close_hour: 12, close_minutes: 0, closed_all_day: false };
  }
  return diaVazio(0); // domingo: folga
};

const carregar = async () => {
  sujo.value = false;
  carregouOk.value = false;
  if (modoCriacao.value) {
    // Agente ainda não existe: não busca a jornada. Parte do padrão, que o submit
    // grava. Ainda assim busca o fuso (sem user) p/ a linha de ajuda.
    semana.value = DIAS.map(diaPadrao);
    try {
      const { data } = await AgentScheduleAPI.get();
      fuso.value = data.fuso || '';
    } catch (e) {
      /* sem fuso -> linha some, sem chute */
    }
    return;
  }
  semana.value = DIAS.map(diaVazio);
  try {
    const { data } = await AgentScheduleAPI.get(props.userId);
    (data.dias || []).forEach(salvo => {
      const i = semana.value.findIndex(d => d.day_of_week === salvo.day_of_week);
      if (i >= 0) semana.value[i] = { ...semana.value[i], ...salvo };
    });
    fuso.value = data.fuso || '';
    carregouOk.value = true;
  } catch (e) {
    // GET falhou: a semana fica FECHADA (baseline). carregouOk continua false,
    // então o submit do modal NÃO vai sobrescrever a jornada real com isso.
  }
};

// Checkbox = TRABALHA (como na tela nativa da caixa). No banco a coluna segue
// `closed_all_day` — a tela mostra o contrário dela. Inverter a coluna seria
// migração sem motivo e o cálculo (Fila::Expediente) depende dela como está.
const alternarDia = dia => {
  dia.closed_all_day = !dia.closed_all_day;
  sujo.value = true;
};

const definirHora = (dia, campo, valor) => {
  const [h, m] = valor.split(':').map(Number);
  dia[`${campo}_hour`] = h;
  dia[`${campo}_minutes`] = m;
  // Abertura >= fechamento: empurra o fechamento p/ abertura + 1h (teto 23:30).
  // Evita o ida-e-volta com o erro do servidor (horario_coerente), que segue
  // sendo a autoridade.
  if (campo === 'open') {
    const abre = dia.open_hour * 60 + dia.open_minutes;
    const fecha = dia.close_hour * 60 + dia.close_minutes;
    if (abre >= fecha) {
      const novo = Math.min(abre + 60, 23 * 60 + 30);
      dia.close_hour = Math.floor(novo / 60);
      dia.close_minutes = novo % 60;
    }
  }
  sujo.value = true;
};

// Total de horas (conta no frontend: fechamento − abertura).
const minutosDia = dia =>
  dia.closed_all_day ? 0 : dia.close_hour * 60 + dia.close_minutes - (dia.open_hour * 60 + dia.open_minutes);
const formatarHoras = min => {
  const total = Math.max(0, min);
  const h = Math.floor(total / 60);
  const m = total % 60;
  const base = t('AGENT_SCHEDULE.HORAS', { h });
  return m ? `${base} ${m}min` : base;
};
const totalDia = dia => (dia.closed_all_day ? '—' : formatarHoras(minutosDia(dia)));
const totalSemana = computed(() =>
  formatarHoras(semana.value.reduce((s, d) => s + minutosDia(d), 0))
);

const salvar = async () => {
  salvando.value = true;
  try {
    await AgentScheduleAPI.save(props.userId, semana.value);
    useAlert(t('AGENT_SCHEDULE.SALVO'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENT_SCHEDULE.ERRO'));
  } finally {
    salvando.value = false;
  }
};

// Só grava quando há motivo: na criação sempre (o padrão pré-preenchido é pra
// salvar); na edição, só se carregou com sucesso E o usuário mexeu em algo.
// Sem isso, abrir um agente só pra corrigir o nome e clicar em "Editar agente"
// substituiria a jornada real pela semana fechada do baseline — silenciosamente.
const precisaSalvar = computed(() => modoCriacao.value || (carregouOk.value && sujo.value));

// Gravação disparada pelo PAI (criação sempre; edição só se precisaSalvar). A
// lógica de API fica aqui (um lugar só fala com ela); o erro NÃO é engolido —
// sobe pro modal, que avisa honestamente se a jornada não salvou.
const salvarPara = novoUserId => AgentScheduleAPI.save(novoUserId, semana.value);
const salvarSeNecessario = async userId => {
  if (!precisaSalvar.value) return false;
  await salvarPara(userId);
  return true;
};
defineExpose({ salvarPara, salvarSeNecessario, precisaSalvar: () => precisaSalvar.value });

watch(() => props.userId, carregar);
onMounted(carregar);
</script>

<template>
  <div class="mt-6 border border-n-weak rounded-lg">
    <button
      type="button"
      class="w-full flex items-center justify-between px-4 py-3 text-sm text-n-slate-12"
      @click="aberto = !aberto"
    >
      <span class="font-medium">{{ t('AGENT_SCHEDULE.TITULO') }}</span>
      <span class="text-n-slate-11">{{ aberto ? '−' : '+' }}</span>
    </button>

    <div v-if="aberto" class="px-4 pb-4">
      <p class="text-xs text-n-slate-11 mb-1">{{ t('AGENT_SCHEDULE.AJUDA') }}</p>
      <!-- Fuso vindo do servidor (Fila::Expediente.fuso). Vazio (imagem antiga) =>
           a linha some, nunca um fuso chutado na tela. Não hardcodar "Brasília". -->
      <p v-if="fuso" class="text-xs text-n-slate-11 mb-3">
        {{ t('AGENT_SCHEDULE.FUSO', { fuso }) }}
      </p>
      <div v-else class="mb-3"></div>

      <!-- Grade de colunas FIXAS: dia+checkbox (à esquerda) → horários/Indisponível
           (largura fixa, pra "Indisponível" não deslocar o total) → total do dia.
           `shrink-0` em tudo impede o flex de encolher proporcionalmente (era o que
           desalinhava de dia pra dia: "Segunda" é mais largo que "Terça").
           PEGADINHA: os seletores usam `!w-20` (important) — a base do Chatwoot tem
           um `input/select { width: 100% }` global que vence o utilitário do Tailwind
           por especificidade; com `shrink-0` a caixa não encolhe e estourava a 100%
           da linha. O `!` é o padrão do código pra vencer esse global. -->
      <div
        v-for="dia in semana"
        :key="dia.day_of_week"
        class="flex items-center gap-2 py-1.5 text-sm"
      >
        <!-- Checkbox = TRABALHA, e o rótulo é o nome do dia (clicar liga/desliga),
             como na tela nativa da caixa. -->
        <label class="w-28 shrink-0 flex items-center gap-2 text-n-slate-12 cursor-pointer">
          <input
            type="checkbox"
            :checked="!dia.closed_all_day"
            @change="alternarDia(dia)"
          />
          {{ t(`AGENT_SCHEDULE.DIAS.${dia.day_of_week}`) }}
        </label>

        <!-- Horários OU "Indisponível", na MESMA largura, pra o total não deslizar. -->
        <div class="w-[17rem] shrink-0 flex items-center gap-2">
          <template v-if="!dia.closed_all_day">
            <template v-for="campo in ['open', 'close']" :key="campo">
              <span class="shrink-0 text-n-slate-11">
                {{ campo === 'open' ? t('AGENT_SCHEDULE.DAS') : t('AGENT_SCHEDULE.AS') }}
              </span>
              <select
                :value="hhmm(dia[`${campo}_hour`], dia[`${campo}_minutes`])"
                class="!w-20 shrink-0 px-1 py-0.5 rounded border border-n-weak"
                @change="definirHora(dia, campo, $event.target.value)"
              >
                <option
                  v-for="opt in opcoesHora(dia[`${campo}_hour`], dia[`${campo}_minutes`])"
                  :key="opt"
                  :value="opt"
                >
                  {{ opt }}
                </option>
              </select>
            </template>
          </template>
          <span v-else class="text-n-slate-11">{{ t('AGENT_SCHEDULE.INDISPONIVEL') }}</span>
        </div>

        <!-- Total do dia, à direita (como o "14h" da tela nativa). -->
        <span class="w-16 shrink-0 text-right text-n-slate-11">{{ totalDia(dia) }}</span>
      </div>

      <p class="mt-3 text-sm font-medium text-n-slate-12">
        {{ t('AGENT_SCHEDULE.SEMANA_PREVISTA', { horas: totalSemana }) }}
      </p>

      <!-- Botão próprio: só no relatório (mostrarBotao) e fora da criação. No modal
           de editar agente (mostrarBotao=false) quem grava é o submit do modal. -->
      <button
        v-if="mostrarBotao && !modoCriacao"
        type="button"
        class="mt-3 px-3 py-1.5 rounded bg-n-brand text-white text-sm disabled:opacity-50"
        :disabled="salvando"
        @click="salvar"
      >
        {{ salvando ? t('AGENT_SCHEDULE.SALVANDO') : t('AGENT_SCHEDULE.SALVAR') }}
      </button>
    </div>
  </div>
</template>
